import { useEffect } from 'react';
import SeoHead from '../../components/feature/SeoHead';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const jsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Compra de Coches Sin ITV en Barcelona',
    description: 'Compramos coches sin ITV en Barcelona. ITV caducada, no pasa la inspección, coche abandonado. Recogida gratuita y gestión legal completa.',
    provider: {
      '@type': 'AutoDealer',
      name: 'Desguace Barcelona',
      telephone: '+34932932913',
      address: { '@type': 'PostalAddress', addressLocality: 'Barcelona', addressCountry: 'ES' },
    },
    areaServed: { '@type': 'AdministrativeArea', name: 'Provincia de Barcelona' },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '¿Es legal vender un coche sin ITV en Barcelona?',
        acceptedAnswer: { '@type': 'Answer', text: 'Sí, es completamente legal vender un coche sin ITV a un desguace autorizado. Lo que no es legal es circular con él por la vía pública. Al vendérnoslo, tramitamos la baja definitiva y te entregamos el Certificado de Destrucción.' },
      },
      {
        '@type': 'Question',
        name: '¿Puedo vender mi coche si la ITV está caducada hace años?',
        acceptedAnswer: { '@type': 'Answer', text: 'Sí, sin problema. No importa cuánto tiempo lleve caducada la ITV. Te compramos el coche igualmente y nos encargamos de todos los trámites de baja.' },
      },
      {
        '@type': 'Question',
        name: '¿Qué documentos necesito para vender un coche sin ITV?',
        acceptedAnswer: { '@type': 'Answer', text: 'Necesitas tu DNI o NIE, el permiso de circulación (tarjeta verde) y la ficha técnica del vehículo. No necesitas tener la ITV en vigor.' },
      },
      {
        '@type': 'Question',
        name: '¿Cuánto tarda el proceso de venta de un coche sin ITV?',
        acceptedAnswer: { '@type': 'Answer', text: 'El proceso es muy rápido. Desde que nos contactas hasta que recogemos el coche y te pagamos, suelen pasar entre 24 y 48 horas.' },
      },
    ],
  },
];

export default function VenderCocheSinITVPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title="Vender Coche Sin ITV en Barcelona | ITV Caducada | Desguace Barcelona"
        description="Compramos coches sin ITV en Barcelona. ITV caducada o no pasa la inspección, te lo compramos al mejor precio. Recogida gratis, gestión legal completa y pago inmediato."
        keywords="vender coche sin ITV Barcelona, coche ITV caducada Barcelona, coche no pasa ITV Barcelona, desguace sin ITV Barcelona"
        canonical={`${siteUrl}/vender-coche-sin-itv-barcelona`}
        jsonLd={jsonLd}
      />
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'url(https://public.readdy.ai/ai/img_res/ecf2d3154d092cbe7f56ca2bea20aa6c.jpg)', backgroundSize: 'cover', backgroundPosition: 'center' }}></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Vender Coche Sin ITV en Barcelona
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              ¿Tu coche tiene la ITV caducada o no pasa la inspección? Te lo compramos al mejor precio, sin complicaciones legales y con recogida gratuita en toda Barcelona y provincia.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a 
                href="https://wa.me/34932932913?text=Hola%20Desguaces%20Barcelona%2C%20quiero%20valorar%20mi%20coche%20sin%20ITV" 
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center justify-center px-10 py-5 bg-[#25D366] text-white rounded-full font-bold text-lg hover:bg-[#1fb855] transition-all shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line mr-3 text-2xl"></i>
                WhatsApp
              </a>
              <a 
                href="tel:+34932932913" 
                className="inline-flex items-center justify-center px-10 py-5 bg-white text-gray-900 rounded-full font-bold text-lg hover:bg-gray-100 transition-all shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-3"></i>
                932 932 913
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Signals */}
      <section className="py-12 bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#ff6b35] rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-money-euro-circle-line text-3xl text-white"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">Mejor Precio</h3>
              <p className="text-sm text-gray-600">Garantizado</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#ff6b35] rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-truck-line text-3xl text-white"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">Recogida Gratis</h3>
              <p className="text-sm text-gray-600">En toda Barcelona</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#ff6b35] rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-time-line text-3xl text-white"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">Proceso Rápido</h3>
              <p className="text-sm text-gray-600">24-48 horas</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#ff6b35] rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-shield-check-line text-3xl text-white"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">100% Legal</h3>
              <p className="text-sm text-gray-600">Con certificado</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content - SEO Optimized */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">¿Por Qué Vender tu Coche Sin ITV a Nuestro Desguace en Barcelona?</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Tener un <strong>coche sin ITV en Barcelona</strong> puede convertirse en un verdadero problema. Ya sea porque la inspección técnica está caducada, porque el vehículo no pasa la ITV por problemas mecánicos graves, o simplemente porque no quieres invertir más dinero en reparaciones costosas, vender tu coche a un desguace profesional es la solución más rápida, legal y rentable.
            </p>
            <p className="text-gray-700 leading-relaxed mb-8">
              En nuestro <strong>desguace en Barcelona</strong>, nos especializamos en la compra de coches sin ITV, ofreciendo un servicio completo que incluye tasación gratuita, recogida sin coste en toda la provincia de Barcelona, y gestión de toda la documentación legal necesaria para dar de baja el vehículo correctamente.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-6 mt-12">Situaciones Comunes de Coches Sin ITV en Barcelona</h2>
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                <div className="w-12 h-12 bg-[#ff6b35] rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-calendar-close-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">ITV Caducada</h3>
                <p className="text-gray-700">
                  Tu coche lleva meses o años sin pasar la inspección técnica y acumula multas. Te lo compramos sin problemas y gestionamos la baja definitiva.
                </p>
              </div>
              <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                <div className="w-12 h-12 bg-[#ff6b35] rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-close-circle-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">No Pasa la ITV</h3>
                <p className="text-gray-700">
                  El vehículo tiene defectos graves que impiden superar la inspección y las reparaciones son demasiado costosas. Vendérnoslo es la mejor opción.
                </p>
              </div>
              <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                <div className="w-12 h-12 bg-[#ff6b35] rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-parking-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Coche Abandonado</h3>
                <p className="text-gray-700">
                  El vehículo lleva tiempo parado en un garaje o en la calle sin uso. Evita sanciones municipales vendiéndolo a nuestro desguace.
                </p>
              </div>
              <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                <div className="w-12 h-12 bg-[#ff6b35] rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-tools-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Reparación Muy Costosa</h3>
                <p className="text-gray-700">
                  Para pasar la ITV necesitas invertir más dinero del que vale el coche. Mejor venderlo y recuperar algo de inversión.
                </p>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mb-6 mt-12">Información Legal: Vender Coche Sin ITV en Barcelona</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Es completamente <strong>legal vender un coche sin ITV a un desguace autorizado</strong> en Barcelona. De hecho, es la forma más segura y recomendada de deshacerte de un vehículo que no puede circular legalmente por la vía pública.
            </p>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-8">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                <i className="ri-information-line text-blue-500 mr-2 text-xl"></i>
                Aspectos Legales Importantes
              </h4>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-green-500 mr-2 mt-1"></i>
                  <span><strong>No puedes circular</strong> con un coche sin ITV vigente por las calles de Barcelona (multa de hasta 200€)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-green-500 mr-2 mt-1"></i>
                  <span><strong>Puedes venderlo a un desguace</strong> sin necesidad de pasar la ITV previamente</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-green-500 mr-2 mt-1"></i>
                  <span><strong>Nosotros gestionamos la baja definitiva</strong> ante la DGT y el Ayuntamiento de Barcelona</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-green-500 mr-2 mt-1"></i>
                  <span><strong>Recibirás el Certificado de Destrucción</strong> que te exime de futuras responsabilidades</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-green-500 mr-2 mt-1"></i>
                  <span><strong>Dejarás de pagar impuestos</strong> del vehículo una vez tramitada la baja</span>
                </li>
              </ul>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mb-6 mt-12">Ventajas de Vender tu Coche Sin ITV a Nuestro Desguace</h2>
            <div className="space-y-4 mb-8">
              <div className="flex items-start bg-gray-50 p-5 rounded-lg">
                <div className="w-10 h-10 bg-[#ff6b35] rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-money-euro-circle-line text-xl text-white"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Obtienes Dinero Inmediato</h4>
                  <p className="text-gray-700">En lugar de tener un coche parado perdiendo valor, lo conviertes en efectivo al instante. Te pagamos en el momento de la recogida.</p>
                </div>
              </div>
              <div className="flex items-start bg-gray-50 p-5 rounded-lg">
                <div className="w-10 h-10 bg-[#ff6b35] rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-file-text-line text-xl text-white"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Gestión Completa de Trámites</h4>
                  <p className="text-gray-700">Nos encargamos de toda la documentación: baja definitiva ante Tráfico, notificación al Ayuntamiento de Barcelona, y entrega del Certificado de Destrucción.</p>
                </div>
              </div>
              <div className="flex items-start bg-gray-50 p-5 rounded-lg">
                <div className="w-10 h-10 bg-[#ff6b35] rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-shield-check-line text-xl text-white"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Evitas Multas y Sanciones</h4>
                  <p className="text-gray-700">Un coche sin ITV puede generar multas de tráfico, sanciones municipales por abandono, y seguir pagando impuestos. Vendérnoslo soluciona todos estos problemas.</p>
                </div>
              </div>
              <div className="flex items-start bg-gray-50 p-5 rounded-lg">
                <div className="w-10 h-10 bg-[#ff6b35] rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-truck-line text-xl text-white"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Recogida Gratuita en Barcelona</h4>
                  <p className="text-gray-700">No necesitas mover el coche. Vamos a buscarlo donde esté: garaje, parking, calle (con permisos), o cualquier ubicación en Barcelona y provincia.</p>
                </div>
              </div>
              <div className="flex items-start bg-gray-50 p-5 rounded-lg">
                <div className="w-10 h-10 bg-[#ff6b35] rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-time-line text-xl text-white"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Proceso Rápido (24-48h)</h4>
                  <p className="text-gray-700">Desde que nos contactas hasta que recogemos el coche y te pagamos, solo pasan 1 o 2 días. Sin esperas ni complicaciones.</p>
                </div>
              </div>
              <div className="flex items-start bg-gray-50 p-5 rounded-lg">
                <div className="w-10 h-10 bg-[#ff6b35] rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-recycle-line text-xl text-white"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Reciclaje Ecológico</h4>
                  <p className="text-gray-700">Somos un Centro Autorizado de Tratamiento de Vehículos (CAT). Reciclamos el coche de forma responsable cumpliendo toda la normativa ambiental.</p>
                </div>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mb-6 mt-12">Proceso para Vender tu Coche Sin ITV en Barcelona</h2>
            <div className="space-y-6 mb-8">
              <div className="flex items-start">
                <div className="w-12 h-12 bg-[#ff6b35] rounded-full flex items-center justify-center mr-4 flex-shrink-0 text-white font-bold text-xl">
                  1
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">Contacta con Nosotros</h4>
                  <p className="text-gray-700">Llámanos al <strong>932 932 913</strong> o rellena el formulario de tasación gratuita. Indícanos la marca, modelo, año y estado del coche (aunque no tenga ITV).</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-12 h-12 bg-[#ff6b35] rounded-full flex items-center justify-center mr-4 flex-shrink-0 text-white font-bold text-xl">
                  2
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">Tasación Inmediata</h4>
                  <p className="text-gray-700">Te damos un precio justo al instante. Valoramos el coche según su estado real, piezas aprovechables y peso del metal. No importa que no tenga ITV vigente.</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-12 h-12 bg-[#ff6b35] rounded-full flex items-center justify-center mr-4 flex-shrink-0 text-white font-bold text-xl">
                  3
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">Acordamos Recogida</h4>
                  <p className="text-gray-700">Fijamos día y hora para recoger el vehículo en la ubicación que nos indiques en Barcelona o provincia. La recogida es totalmente gratuita.</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-12 h-12 bg-[#ff6b35] rounded-full flex items-center justify-center mr-4 flex-shrink-0 text-white font-bold text-xl">
                  4
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">Recogida y Pago</h4>
                  <p className="text-gray-700">Nuestro equipo va a buscar el coche con grúa. Verificamos la documentación (DNI, permiso de circulación, ficha técnica) y te pagamos en efectivo o transferencia al momento.</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-12 h-12 bg-[#ff6b35] rounded-full flex items-center justify-center mr-4 flex-shrink-0 text-white font-bold text-xl">
                  5
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">Gestión de Baja y Certificado</h4>
                  <p className="text-gray-700">Tramitamos la baja definitiva del vehículo ante la DGT y te enviamos el <strong>Certificado de Destrucción</strong> en pocos días. Ya no tendrás ninguna responsabilidad sobre el coche.</p>
                </div>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mb-6 mt-12">¿Qué Documentación Necesitas para Vender un Coche Sin ITV?</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Aunque el coche no tenga la ITV en vigor, sí necesitas tener la documentación básica del vehículo para poder venderlo legalmente a nuestro desguace en Barcelona:
            </p>
            <ul className="space-y-3 mb-8">
              <li className="flex items-start text-gray-700">
                <i className="ri-file-text-line text-[#ff6b35] mr-3 mt-1 text-xl"></i>
                <span><strong>DNI o NIE del propietario</strong> (original y en vigor)</span>
              </li>
              <li className="flex items-start text-gray-700">
                <i className="ri-file-text-line text-[#ff6b35] mr-3 mt-1 text-xl"></i>
                <span><strong>Permiso de Circulación</strong> (tarjeta verde del coche)</span>
              </li>
              <li className="flex items-start text-gray-700">
                <i className="ri-file-text-line text-[#ff6b35] mr-3 mt-1 text-xl"></i>
                <span><strong>Ficha Técnica</strong> del vehículo</span>
              </li>
              <li className="flex items-start text-gray-700">
                <i className="ri-file-text-line text-[#ff6b35] mr-3 mt-1 text-xl"></i>
                <span><strong>Último recibo del impuesto de circulación</strong> (no es obligatorio pero ayuda)</span>
              </li>
            </ul>
            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 mb-8">
              <p className="text-gray-700">
                <strong>Nota importante:</strong> Si has perdido algún documento, podemos ayudarte a gestionarlo. En algunos casos es posible tramitar duplicados o realizar la venta con documentación alternativa. Consúltanos tu caso específico.
              </p>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mb-6 mt-12">Zonas de Barcelona Donde Recogemos Coches Sin ITV</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Ofrecemos servicio de <strong>recogida gratuita de coches sin ITV</strong> en toda la ciudad de Barcelona y en toda la provincia. Algunas de las zonas donde operamos habitualmente:
            </p>
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">Barcelona Capital</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Eixample</li>
                  <li>• Gràcia</li>
                  <li>• Sants-Montjuïc</li>
                  <li>• Les Corts</li>
                  <li>• Sarrià-Sant Gervasi</li>
                  <li>• Horta-Guinardó</li>
                  <li>• Nou Barris</li>
                  <li>• Sant Andreu</li>
                  <li>• Sant Martí</li>
                  <li>• Ciutat Vella</li>
                </ul>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">Área Metropolitana</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Hospitalet de Llobregat</li>
                  <li>• Badalona</li>
                  <li>• Santa Coloma de Gramenet</li>
                  <li>• Cornellà de Llobregat</li>
                  <li>• Sant Boi de Llobregat</li>
                  <li>• Esplugues de Llobregat</li>
                  <li>• Viladecans</li>
                  <li>• Gavà</li>
                  <li>• Castelldefels</li>
                </ul>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">Provincia Barcelona</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Sabadell</li>
                  <li>• Terrassa</li>
                  <li>• Mataró</li>
                  <li>• Granollers</li>
                  <li>• Manresa</li>
                  <li>• Vilanova i la Geltrú</li>
                  <li>• Vilafranca del Penedès</li>
                  <li>• Mollet del Vallès</li>
                  <li>• Cerdanyola del Vallès</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">Preguntas Frecuentes sobre Vender Coche Sin ITV</h2>
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-start">
                <i className="ri-question-line text-[#ff6b35] mr-3 mt-1"></i>
                ¿Es legal vender un coche sin ITV en Barcelona?
              </h3>
              <p className="text-gray-700 ml-9">
                Sí, es completamente legal vender un coche sin ITV a un desguace autorizado como el nuestro. Lo que no es legal es circular con él por la vía pública. Al vendérnoslo, nosotros tramitamos la baja definitiva del vehículo de forma legal y te entregamos el Certificado de Destrucción.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-start">
                <i className="ri-question-line text-[#ff6b35] mr-3 mt-1"></i>
                ¿Puedo vender mi coche si la ITV está caducada hace años?
              </h3>
              <p className="text-gray-700 ml-9">
                Sí, sin problema. No importa cuánto tiempo lleve caducada la ITV. Te compramos el coche igualmente y nos encargamos de todos los trámites de baja. Muchos de nuestros clientes en Barcelona venden coches con la ITV caducada desde hace 2, 3 o más años.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-start">
                <i className="ri-question-line text-[#ff6b35] mr-3 mt-1"></i>
                ¿Qué pasa con las multas acumuladas por no tener ITV?
              </h3>
              <p className="text-gray-700 ml-9">
                Las multas de tráfico por circular sin ITV son responsabilidad del propietario y deben pagarse. Sin embargo, al vender el coche y tramitar la baja definitiva, dejarás de acumular nuevas multas y sanciones. También dejarás de pagar el impuesto de circulación.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-start">
                <i className="ri-question-line text-[#ff6b35] mr-3 mt-1"></i>
                ¿Cuánto tarda el proceso de venta de un coche sin ITV?
              </h3>
              <p className="text-gray-700 ml-9">
                El proceso es muy rápido. Desde que nos contactas hasta que recogemos el coche y te pagamos, suelen pasar entre 24 y 48 horas. La baja definitiva ante Tráfico la tramitamos nosotros en los días siguientes, y te enviamos el Certificado de Destrucción por correo.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-start">
                <i className="ri-question-line text-[#ff6b35] mr-3 mt-1"></i>
                ¿Recogéis el coche si no arranca o está en mal estado?
              </h3>
              <p className="text-gray-700 ml-9">
                Sí, recogemos coches en cualquier estado: sin ITV, que no arrancan, con averías graves, accidentados, etc. Utilizamos grúa para el transporte, así que no importa si el coche no puede moverse por sí mismo. La recogida es gratuita en toda Barcelona y provincia.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-start">
                <i className="ri-question-line text-[#ff6b35] mr-3 mt-1"></i>
                ¿Qué documentos necesito para vender un coche sin ITV?
              </h3>
              <p className="text-gray-700 ml-9">
                Necesitas tu DNI o NIE, el permiso de circulación (tarjeta verde) y la ficha técnica del vehículo. No necesitas tener la ITV en vigor ni el último recibo del seguro. Si has perdido algún documento, consúltanos y te ayudamos a gestionarlo.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-start">
                <i className="ri-question-line text-[#ff6b35] mr-3 mt-1"></i>
                ¿Cuánto me pagaréis por un coche sin ITV?
              </h3>
              <p className="text-gray-700 ml-9">
                El precio depende de varios factores: marca, modelo, año, estado general, piezas aprovechables y peso del metal. Aunque no tenga ITV, si el coche tiene piezas en buen estado, el valor será mayor. Te damos una tasación gratuita y sin compromiso al instante.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-start">
                <i className="ri-question-line text-[#ff6b35] mr-3 mt-1"></i>
                ¿Es mejor vender el coche sin ITV o intentar pasarla?
              </h3>
              <p className="text-gray-700 ml-9">
                Depende del estado del coche. Si las reparaciones necesarias para pasar la ITV cuestan más de lo que vale el vehículo, es mejor venderlo a un desguace. Te ahorras el coste de las reparaciones, la ITV, y obtienes dinero inmediato. Además, evitas seguir pagando impuestos y seguro.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-start">
                <i className="ri-question-line text-[#ff6b35] mr-3 mt-1"></i>
                ¿Puedo vender un coche sin ITV si no está a mi nombre?
              </h3>
              <p className="text-gray-700 ml-9">
                No, solo el propietario legal del vehículo puede venderlo. Si el coche está a nombre de otra persona (familiar fallecido, por ejemplo), primero hay que hacer el cambio de titularidad en Tráfico. Podemos asesorarte sobre cómo hacerlo.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-start">
                <i className="ri-question-line text-[#ff6b35] mr-3 mt-1"></i>
                ¿Qué diferencia hay entre vender a un desguace y a un particular?
              </h3>
              <p className="text-gray-700 ml-9">
                Vender un coche sin ITV a un particular es muy difícil y arriesgado, ya que el comprador no podrá circular con él legalmente. En cambio, venderlo a un desguace autorizado como el nuestro es rápido, legal y seguro. Nosotros nos encargamos de todo: recogida, pago inmediato y baja definitiva del vehículo.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final Section */}
      <section id="tasacion" className="py-20 bg-gradient-to-br from-[#ff6b35] to-[#e55a2b]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">¿Tienes un Coche Sin ITV en Barcelona?</h2>
          <p className="text-xl text-white/90 mb-8">
            Obtén una tasación gratuita ahora mismo. Te compramos el coche, gestionamos la baja y te pagamos al instante.
          </p>
          <div className="bg-white rounded-2xl p-8 shadow-2xl max-w-2xl mx-auto">
            <form className="space-y-4" data-readdy-form>
              <div className="grid md:grid-cols-2 gap-4">
                <input 
                  type="text" 
                  name="nombre"
                  placeholder="Tu nombre" 
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#ff6b35] focus:ring-2 focus:ring-[#ff6b35]/20 outline-none transition-all text-sm"
                  required
                />
                <input 
                  type="tel" 
                  name="telefono"
                  placeholder="Teléfono" 
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#ff6b35] focus:ring-2 focus:ring-[#ff6b35]/20 outline-none transition-all text-sm"
                  required
                />
              </div>
              <input 
                type="email" 
                name="email"
                placeholder="Email" 
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#ff6b35] focus:ring-2 focus:ring-[#ff6b35]/20 outline-none transition-all text-sm"
                required
              />
              <div className="grid md:grid-cols-2 gap-4">
                <input 
                  type="text" 
                  name="marca"
                  placeholder="Marca del coche" 
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#ff6b35] focus:ring-2 focus:ring-[#ff6b35]/20 outline-none transition-all text-sm"
                  required
                />
                <input 
                  type="text" 
                  name="modelo"
                  placeholder="Modelo" 
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#ff6b35] focus:ring-2 focus:ring-[#ff6b35]/20 outline-none transition-all text-sm"
                  required
                />
              </div>
              <input 
                type="text" 
                name="año"
                placeholder="Año del vehículo" 
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#ff6b35] focus:ring-2 focus:ring-[#ff6b35]/20 outline-none transition-all text-sm"
                required
              />
              <textarea 
                name="detalles"
                placeholder="Cuéntanos el estado del coche (ITV caducada, no pasa ITV, etc.)" 
                rows={4}
                maxLength={500}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#ff6b35] focus:ring-2 focus:ring-[#ff6b35]/20 outline-none transition-all resize-none text-sm"
                required
              ></textarea>
              <button 
                type="submit" 
                className="w-full bg-[#ff6b35] text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-[#e55a2b] transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer transform hover:scale-105"
              >
                Solicitar Tasación Gratuita
              </button>
            </form>
          </div>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a 
              href="tel:+34932932913" 
              className="flex items-center space-x-2 text-white hover:text-white/80 transition-colors cursor-pointer"
            >
              <i className="ri-phone-line text-2xl"></i>
              <span className="font-semibold">932 932 913</span>
            </a>
            <span className="text-white/60 hidden sm:block">|</span>
            <a 
              href="https://wa.me/34932932913" 
              className="flex items-center space-x-2 text-white hover:text-white/80 transition-colors cursor-pointer"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="ri-whatsapp-line text-2xl"></i>
              <span className="font-semibold">WhatsApp</span>
            </a>
          </div>
          <p className="text-white/80 text-sm mt-6">
            Horario: Lunes a Viernes 9:00 - 19:00 | Sábados 9:00 - 14:00
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
